import { useState, useEffect } from 'react';
import { getHolidayMap, getHolidayMapSync } from '@/utils/holidays';

/**
 * Hook that provides a holiday map for a given year and country.
 * Returns sync cache immediately (no flicker), then updates async from Nager.Date API.
 */
export function useHolidayMap(year: number, countryCode?: string): Map<string, string> {
  const [holidayMap, setHolidayMap] = useState<Map<string, string>>(
    () => getHolidayMapSync(year, countryCode)
  );

  useEffect(() => {
    let cancelled = false;
    // Start with sync cache for immediate render
    setHolidayMap(getHolidayMapSync(year, countryCode));

    // Then fetch async (from cache or API)
    getHolidayMap(year, countryCode).then((map) => {
      if (!cancelled) setHolidayMap(map);
    });

    return () => { cancelled = true; };
  }, [year, countryCode]);

  return holidayMap;
}

/**
 * Hook that provides holiday maps for multiple years (useful for calendar boundary months).
 * Merges all years into a single map.
 */
export function useHolidayMapMultiYear(years: number[], countryCode?: string): Map<string, string> {
  const [holidayMap, setHolidayMap] = useState<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const y of years) {
      for (const [date, name] of getHolidayMapSync(y, countryCode)) {
        map.set(date, name);
      }
    }
    return map;
  });

  useEffect(() => {
    let cancelled = false;

    // Sync cache first
    const syncMap = new Map<string, string>();
    for (const y of years) {
      for (const [date, name] of getHolidayMapSync(y, countryCode)) {
        syncMap.set(date, name);
      }
    }
    setHolidayMap(syncMap);

    // Async fetch all years
    Promise.all(years.map((y) => getHolidayMap(y, countryCode))).then((maps) => {
      if (cancelled) return;
      const merged = new Map<string, string>();
      for (const m of maps) {
        for (const [date, name] of m) {
          merged.set(date, name);
        }
      }
      setHolidayMap(merged);
    });

    return () => { cancelled = true; };
  }, [years.join(','), countryCode]);

  return holidayMap;
}
